import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, UsersIcon } from 'lucide-react';
import { AnimatedNumber } from './AnimatedNumber';
import type { PeerOption } from '../types/pulse';
interface PeerComparisonCardProps {
  options: PeerOption[];
  selectedId: string;
  accent: string;
  peerGroup: string;
}
export function PeerComparisonCard({
  options,
  selectedId,
  accent,
  peerGroup
}: PeerComparisonCardProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.id === selectedId);
  if (!selected) return null;
  const sorted = [...options].sort((a, b) => b.peerPercent - a.peerPercent);
  const top = sorted[0];
  const isMostCommon = top.id === selected.id;
  return <section aria-label="Peer comparison" className="rounded-3xl border border-navy-900/[0.08] bg-white p-5 shadow-[0_8px_30px_-12px_rgba(15,36,66,0.15)]" data-id="element-418" data-mp="3dbsy5uk9vz0e">
      <div className="flex items-center justify-between gap-3" data-id="element-419" data-mp="21we0eok8p348">
        <p className="flex items-center gap-2 text-[12px] font-medium text-slate-600" data-id="element-420" data-mp="28g6r6fxuxc9f">
          <UsersIcon className="h-3.5 w-3.5" style={{
          color: accent
        }} data-id="element-421" data-mp="23if01o51du6a" />
          Compared with {peerGroup}
        </p>
        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-800" data-id="element-422" data-mp="2hiprote67csi">
          Illustrative
        </span>
      </div>

      <p className="mt-4 text-[11px] uppercase tracking-[0.14em] text-slate-400" data-id="element-423" data-mp="2rf16pr55agqn">You answered</p>
      <p className="mt-1 font-serif text-[19px] leading-snug text-navy-900" data-id="element-424" data-mp="2mh9curkxwkjt">{selected.label}</p>

      <div className="mt-5 flex items-end gap-3" data-id="element-425" data-mp="308nxt3yeqvti">
        <p className="font-serif text-[56px] font-light leading-none" style={{
        color: accent
      }} data-id="element-426" data-mp="307cz3ijtke99">
          <AnimatedNumber value={selected.peerPercent} delay={0.3} data-id="element-427" data-mp="2w7cf7idybaut" />%
        </p>
        <p className="pb-2 text-[11px] font-semibold uppercase leading-tight tracking-[0.16em] text-navy-800" data-id="element-428" data-mp="3a6tzplk7s1yd">
          Answered
          <br data-id="element-429" data-mp="355ed3p2e1ndv" />
          like you
        </p>
      </div>

      <div className="relative mt-4 h-3 w-full overflow-hidden rounded-full bg-mist-200" data-id="element-430" data-mp="22v4cumi23j36">
        <motion.div className="h-full rounded-full" style={{
        backgroundColor: accent
      }} initial={{
        width: 0
      }} animate={{
        width: `${selected.peerPercent}%`
      }} transition={{
        duration: 1.4,
        delay: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-431" data-mp="26wg6r47yqqh6" />
      </div>

      <p className="mt-3 text-[13px] leading-relaxed text-slate-500" data-id="element-432" data-mp="2lj9d3jzi8lx8">
        {isMostCommon ? `You share the most common view among participating ${peerGroup}.` : `A less common view — the most frequent answer was “${top.label}”.`}
      </p>

      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="mt-4 flex w-full items-center justify-between rounded-2xl bg-mist-100 px-4 py-3 text-[13px] font-medium text-navy-900 transition hover:bg-mist-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-900/30" data-id="element-433" data-mp="2zk3psetl591p">
        {open ? 'Hide distribution' : 'Reveal all answers'}
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-434" data-mp="svbcb0gh40ls" />
      </button>

      <AnimatePresence initial={false} data-id="element-435" data-mp="nvyp2elowrio">
        {open && <motion.ul initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1]
      }} className="overflow-hidden" data-id="element-436" data-mp="ixniqdulsz57">
            {sorted.map((o, i) => {
          const mine = o.id === selected.id;
          return <li key={o.id} className="pt-4" data-id="element-437" data-mp="12lmspmhcg29k" data-mpi={i}>
                  <div className="flex items-baseline justify-between gap-3 text-[13px]" data-id="element-438" data-mp="12kuyog3eacle">
                    <span className={mine ? 'font-medium text-navy-900' : 'text-slate-500'} data-id="element-439" data-mp="3rei8crme4n1c">
                      {o.label}
                      {mine && <span className="ml-2 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white" style={{
                  backgroundColor: accent
                }} data-id="element-440" data-mp="1f35nlqp2670p">
                          You
                        </span>}
                    </span>
                    <span className="tabular-nums text-slate-600" data-id="element-441" data-mp="1a4m5k1dl5fdm">{o.peerPercent}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-mist-200" data-id="element-442" data-mp="1xrkfeg779p2k">
                    <motion.div className="h-full rounded-full" style={{
                backgroundColor: mine ? accent : '#B6C4D3'
              }} initial={{
                width: 0
              }} animate={{
                width: `${o.peerPercent}%`
              }} transition={{
                duration: 0.9,
                delay: 0.1 + i * 0.08,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-443" data-mp="22qnme66qq5d6" />
                  </div>
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>

      <p className="mt-4 text-[10px] leading-relaxed text-slate-400" data-id="element-444" data-mp="4wtb6pio1m19">
        Demonstration figures only — not genuine survey findings. Voluntary participants are not representative of all specialists.
      </p>
    </section>;
}